import styled, { css } from 'styled-components';
import { useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import { useTranslation } from 'react-i18next';

import { useLocation } from 'shared/hooks/useLocation';
import { variableFontRegular } from 'styles/theme/text';
import { useCountriesQuery } from 'store/api/countries';
import { useWindowSize } from 'shared/hooks/useWindowSize';

const ChangeShoppingRegionModal = dynamic(
  () =>
    import('productTemplate/components/modals/ChangeShoppingRegionModal').then(
      (mod) => mod.ChangeShoppingRegionModal,
    ),
  { ssr: false },
);

const SitePreferencesModal = dynamic(
  () =>
    import('layout/footer/components/SitePreferencesModal').then((mod) => mod.SitePreferencesModal),
  { ssr: false },
);

export const ProductUnavailableInRegion = () => {
  const { t } = useTranslation();
  const { selectedCountryCode } = useLocation();
  const { data } = useCountriesQuery();
  const { isMobile } = useWindowSize();

  const [countryName, setCountryName] = useState('');
  const [isShoppingRegionModalOpen, setIsShoppingRegionModalOpen] = useState(false);
  const [isSitePreferencesModalOpen, setIsSitePreferencesModalOpen] = useState(false);

  const countriesData = data?.countries || [];

  useEffect(() => {
    const currentCountry = countriesData?.find(
      (country) => country.isoCode === selectedCountryCode,
    );
    setCountryName(currentCountry?.name);
  }, [countriesData?.length]);

  const handleCountryClick = () => {
    setIsShoppingRegionModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsShoppingRegionModalOpen(false);
  };

  const handleChangeRegionClick = () => {
    setIsSitePreferencesModalOpen(true);
  };

  return (
    <ProductUnavailableInRegion.Root $isMobile={isMobile} className="bodyTextColumn">
      <div className="headText">
        {t('productTemplate.currentlyUnavailable', 'Currently Unavailable')}
      </div>
      <div className="padTop text row" data-qa="item_not_available_in_region">
        <div>
          {t('productTemplate.changeRegion', 'This item is currently unavailable for purchase in')}
        </div>
        <button type="button" onClick={handleCountryClick}>
          {countryName}.
        </button>
      </div>
      {isShoppingRegionModalOpen && (
        <ChangeShoppingRegionModal
          body={t(
            'productTemplate.unavailableInRegion',
            'To view the best prices or place an offer on this item, please change your current shopping region.',
          )}
          onCloseModal={handleCloseModal}
          onChangeRegionClick={handleChangeRegionClick}
        />
      )}
      {isSitePreferencesModalOpen && (
        <SitePreferencesModal
          isModalOpen={isSitePreferencesModalOpen}
          setIsModalOpen={setIsSitePreferencesModalOpen}
        />
      )}
    </ProductUnavailableInRegion.Root>
  );
};

ProductUnavailableInRegion.Root = styled.div<{ $isMobile: boolean }>`
  ${variableFontRegular({ size: '11.5px', height: '14px' })}
  position: relative;
  height: 100%;

  &.bodyTextColumn {
    ${variableFontRegular({ fontWeight: '400', size: '11px', height: '14px' })}
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  .headText {
    ${variableFontRegular({ fontWeight: '700', size: '11px', height: '14px' })}
    padding-bottom: 10px;
  }
  .padTop {
    padding-top: 14px;
  }
  .text {
    display: flex;

    ${({ $isMobile }) =>
      $isMobile &&
      css`
        color: ${({ theme }) => theme.colors.white};
      `}
  }
  .row {
    flex-direction: row;
  }
  .column {
    flex-direction: column;
  }

  button {
    padding: 0px;
    margin-left: 4px;
    text-decoration: underline;
    text-transform: capitalize;
    cursor: pointer;
  }
`;
